'use client'
import { useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { AUTHORS_DETAIL_PER_PAGE } from 'src/_helpers/constants'
import { ProductTile, Typography } from 'src/components/design-system'
import { getTileBorderClasses, getTileGridClass } from 'src/components/list/getTileBorderClasses'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { searchMeilisearch } from 'src/_services/client/meilisearch.service'
import Button from 'src/components/design-system/Button'
import { meiliHitToProductTile } from 'src/components/list/meiliHitToProductTile'
import { journalIssueService } from 'src/_services/client/journal_issue.service'
import { journalService } from 'src/_services/client/journal.service'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
gsap.registerPlugin(useGSAP)

const safeString = (value) => (typeof value === 'string' ? value.trim() : '')

const getThumbUrl = (entity) => {
  const thumb = entity?.thumbs?.list

  return thumb?.url ? getMediaUrl(thumb.url, thumb.version) : null
}

const getJournalUuid = (item) => safeString(item?.journalUuids?.[0] || item?.journalUuid || item?.journal_uuid)

const getIssueSlug = (item, locale) => safeString(item?.issueSlugs?.[locale] || item?.issueSlug || item?.issue_slug)

const getPublicationId = (item) => safeString(item?.id || item?.uuid)

export default function AuthorsPublications({
  locale,
  initialItems,
  uuid,
  totalItems,
  showTags = false,
  publicationTypes = ['journal_article', 'monographs', 'monograph_chapter'],
  journalUuid = null,
  routeContext = 'main',
}) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [currentPage, setCurrentPage] = useState(1)
  const [publications, setPublications] = useState(initialItems || [])
  const [articleCoverById, setArticleCoverById] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const newItemsRef = useRef([])
  const articleCoverByIdRef = useRef({})

  const publicationTiles = publications
    .map((item, originalIndex) => {
      const tile = meiliHitToProductTile(item, locale, { routeContext, journalRoutes })

      if (!tile) {
        return null
      }

      return {
        ...tile,
        imageSrc: tile.imageSrc || articleCoverById[tile.id] || null,
        tags: showTags ? tile.tags : [],
        originalIndex,
      }
    })
    .filter(Boolean)

  const hasMore = publications.length < totalItems

  useEffect(() => {
    const articlesWithoutCovers = publications
      .map((item) => {
        const id = getPublicationId(item)
        const tile = meiliHitToProductTile(item, locale, { routeContext })

        if (
          item?.type !== 'journal_article' ||
          !id ||
          tile?.imageSrc ||
          articleCoverByIdRef.current[id] !== undefined
        ) {
          return null
        }

        return {
          id,
          issueSlug: getIssueSlug(item, locale),
          journalUuid: getJournalUuid(item),
        }
      })
      .filter(Boolean)

    if (articlesWithoutCovers.length === 0) {
      return
    }

    let isActive = true

    setArticleCoverById((prev) => {
      const next = { ...prev }
      articlesWithoutCovers.forEach(({ id }) => {
        next[id] = null
      })
      articleCoverByIdRef.current = next
      return next
    })

    Promise.all(
      articlesWithoutCovers.map(async ({ id, issueSlug, journalUuid }) => {
        let imageSrc = null

        if (journalUuid && issueSlug) {
          const issue = await journalIssueService.getBySlug(journalUuid, issueSlug, locale).catch(() => null)
          imageSrc = getThumbUrl(issue)
        }

        if (!imageSrc && journalUuid) {
          const journal = await journalService.getByUuid(journalUuid).catch(() => null)
          imageSrc = getThumbUrl(journal)
        }

        return [id, imageSrc]
      }),
    ).then((entries) => {
      if (!isActive) {
        return
      }

      setArticleCoverById((prev) => {
        const next = { ...prev }
        entries.forEach(([id, imageSrc]) => {
          next[id] = imageSrc
        })
        articleCoverByIdRef.current = next
        return next
      })
    })

    return () => {
      isActive = false
    }
  }, [locale, publications])

  useEffect(() => {
    newItemsRef.current = newItemsRef.current.slice(0, publications.length)
  }, [publications.length])

  useGSAP(() => {
    if (currentPage > 1 && newItemsRef.current.length > 0) {
      const startIndex = (currentPage - 1) * AUTHORS_DETAIL_PER_PAGE
      const newElements = newItemsRef.current.slice(startIndex)

      gsap.fromTo(
        newElements,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: 'power2.out',
        },
      )
    }
  }, [currentPage])

  const handleLoadMore = async () => {
    if (isLoading || !hasMore) return

    setIsLoading(true)
    const nextPage = currentPage + 1

    try {
      const params = {
        'authorUuids[]': uuid,
        page: nextPage,
        perPage: AUTHORS_DETAIL_PER_PAGE,
        lang: locale,
        'types[]': publicationTypes,
        ...(journalUuid ? { 'journalUuids[]': journalUuid } : {}),
      }
      const result = await searchMeilisearch(params)

      if (result?.hits) {
        setPublications((prev) => [...prev, ...result.hits])
        setCurrentPage(nextPage)
      }
    } catch (error) {
      console.error('Error loading more publications:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="my-64 d:px-40 px-16">
      <Typography variant="h2-caps-bold" color="text-dark-blue-dark-blue1">
        {translate('T_AUTHOR_PUBLICATIONS')}
      </Typography>

      <div className={`grid ${getTileGridClass(4)} mt-20`}>
        {publicationTiles.map((tile, index) => {
          const bordersClassName = getTileBorderClasses(index, { desktopColumns: 4 })
          const { originalIndex, ...tileProps } = tile

          return (
            <div
              key={tile.id || originalIndex}
              ref={(el) => {
                newItemsRef.current[originalIndex] = el
              }}
              className={`${bordersClassName} py-20 h-full`}
            >
              <ProductTile {...tileProps} />
            </div>
          )
        })}
      </div>

      {hasMore && (
        <Button
          onClick={handleLoadMore}
          disabled={isLoading}
          className="w-fit mt-24 d:mt-56 bg-transparent hover:bg-basic-white relative mx-auto block"
          size="l"
        >
          <span className="flex items-center gap-x-8">
            {isLoading ? translate('T_LOADING') : translate('T_SEE_MORE')}
          </span>
        </Button>
      )}
    </div>
  )
}
